/**
 * DemoHost — always-mounted host for the auto-play cinematic ("Simulează o
 * comandă") and the guided tour ("Tur ghidat"). Mounted once in App, outside
 * the tab switch, so a running cinematic survives navigation.
 *
 * The cinematic drives one synthetic order through the ENTIRE Exemplu SRL flow
 * by writing to Dexie and navigating tabs — the real screens react through
 * `useLiveQuery`, while a warm narration band explains each beat and, for the
 * beats that deserve it (WhatsApp message, message→order, delivery route,
 * invoice), a focused stage layers on top.
 *
 * The choreography (copy, timing controller, Dexie mutations) lives in
 * `@/lib/demoScript`; the visuals live in `@/components/CinematicOverlay`. This
 * file is the conductor: it sequences beats and honours pause / skip / close.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { CinematicCaption, CinematicStageView } from '@/components/CinematicOverlay'
import type { RouteMapHandle, RouteStop } from '@/components/RouteMap'
import { useApp } from '@/context'
import {
  CinematicController,
  type CinematicStage,
  NARRATION,
  RICH_TOUR,
  advanceDemoOrder,
  buildDemoLinii,
  cleanupDemoArtifacts,
  createDemoOrder,
  demoRouteStops,
  dispatchDemoOrder,
  invoiceDemoOrder,
  markDemoMessageTransformed,
  seedDemoMessage,
} from '@/lib/demoScript'

export default function DemoHost() {
  const app = useApp()
  const appRef = useRef(app)
  appRef.current = app
  const { registerDemoRunner, setTourSteps } = app

  const [active, setActive] = useState(false)
  const [paused, setPaused] = useState(false)
  const [chapter, setChapter] = useState(1)
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [stage, setStage] = useState<CinematicStage | null>(null)

  const controllerRef = useRef<CinematicController | null>(null)
  const activeRef = useRef(false)
  const routeRef = useRef<RouteMapHandle | null>(null)
  const routeDoneRef = useRef(false)
  const routeActiveRef = useRef(false)

  const stop = useCallback(() => controllerRef.current?.cancel(), [])
  const onRouteComplete = useCallback(() => {
    routeDoneRef.current = true
  }, [])
  const onRouteArrive = useCallback((_index: number, s: RouteStop) => {
    setText(`Se livrează la ${s.eticheta}…`)
  }, [])

  const runCinematic = useCallback(async () => {
    if (activeRef.current) return
    activeRef.current = true

    const c = new CinematicController()
    controllerRef.current = c
    c.onPaused = (p) => {
      setPaused(p)
      if (routeActiveRef.current) {
        if (p) routeRef.current?.pause()
        else routeRef.current?.play()
      }
    }

    const a = appRef.current
    a.setCaption(null)
    a.setDemoRunning(true)
    setPaused(false)
    setActive(true)

    const show = (ch: number, t: string, x: string, s: CinematicStage | null) => {
      setChapter(ch)
      setTitle(t)
      setText(x)
      setStage(s)
    }
    const say = (t: string, x: string) => {
      setTitle(t)
      setText(x)
    }
    const done = () => c.cancelled

    try {
      await cleanupDemoArtifacts()
      if (done()) return

      // ── intro ──────────────────────────────────────────────────────────
      show(1, NARRATION.intro.title, NARRATION.intro.text, { kind: 'intro' })
      await c.sleep(2600)
      if (done()) return

      // ── 1 · Mesaj — a WhatsApp message types in ────────────────────────
      a.setActiveTab('mesaje')
      await seedDemoMessage()
      show(1, NARRATION.mesaj.title, NARRATION.mesaj.text, { kind: 'phone', typing: true })
      await c.sleep(1700)
      if (done()) return
      setStage({ kind: 'phone', typing: false })
      await c.sleep(2600)
      if (done()) return

      // ── 2 · Transformare — message becomes a priced order ──────────────
      const linii = await buildDemoLinii()
      show(2, NARRATION.transform.title, NARRATION.transform.text, { kind: 'transform', linii })
      await c.sleep(1000)
      if (done()) return
      await createDemoOrder(linii)
      await markDemoMessageTransformed()
      await c.sleep(4200)
      if (done()) return

      // ── 3 · Flux — the kanban advances, live ───────────────────────────
      setStage(null)
      a.setActiveTab('comenzi')
      show(3, NARRATION.fluxNou.title, NARRATION.fluxNou.text, null)
      await c.sleep(1700)
      if (done()) return
      await advanceDemoOrder() // Nou → Confirmat
      say(NARRATION.confirmat.title, NARRATION.confirmat.text)
      await c.sleep(2300)
      if (done()) return
      await advanceDemoOrder() // Confirmat → În pregătire
      say(NARRATION.pregatire.title, NARRATION.pregatire.text)
      await c.sleep(2500)
      if (done()) return

      // ── 4 · Dispecerizare — driver + refrigerated van ──────────────────
      show(4, NARRATION.dispecerizare.title, NARRATION.dispecerizare.text, null)
      await dispatchDemoOrder()
      await c.sleep(2600)
      if (done()) return
      await advanceDemoOrder() // În pregătire → Pe drum
      say(NARRATION.pedrum.title, NARRATION.pedrum.text)
      await c.sleep(2300)
      if (done()) return

      // ── 5 · Livrare — the truck rides the route ────────────────────────
      a.setActiveTab('flota')
      routeDoneRef.current = false
      routeActiveRef.current = true
      show(5, NARRATION.livrare.title, NARRATION.livrare.text, {
        kind: 'route',
        stops: demoRouteStops(),
      })
      await c.sleep(650)
      if (done()) {
        routeActiveRef.current = false
        return
      }
      routeRef.current?.play()
      await c.sleep(400)
      if (!done()) routeRef.current?.play() // idempotent nudge if measuring lagged
      await c.until(() => routeDoneRef.current, 30_000)
      routeActiveRef.current = false
      if (done()) return
      say(NARRATION.livrat.title, NARRATION.livrat.text)
      await advanceDemoOrder() // Pe drum → Livrat
      await c.sleep(2300)
      if (done()) return

      // ── 6 · Facturare — invoice + e-Factura + UIT ──────────────────────
      a.setActiveTab('facturare')
      show(6, NARRATION.factura.title, NARRATION.factura.text, null)
      await c.sleep(1700)
      if (done()) return
      const facturaId = await invoiceDemoOrder() // Livrat → Facturat + factură
      if (facturaId) setStage({ kind: 'invoice', facturaId })
      await c.sleep(5000)
      if (done()) return

      // ── outro ──────────────────────────────────────────────────────────
      show(7, NARRATION.outro.title, NARRATION.outro.text, { kind: 'outro' })
      await c.sleep(4400)
    } finally {
      routeActiveRef.current = false
      routeRef.current?.reset()
      controllerRef.current = null
      activeRef.current = false
      setActive(false)
      setPaused(false)
      setStage(null)
      appRef.current.setDemoRunning(false)
      appRef.current.setCaption(null)
    }
  }, [])

  // keyboard controls while the cinematic is running
  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stop()
      else if (e.key === 'ArrowRight') controllerRef.current?.skip()
      else if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault()
        controllerRef.current?.togglePause()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active, stop])

  // register the cinematic runner + the rich tour once
  useEffect(() => {
    registerDemoRunner(() => {
      void runCinematic()
    })
    setTourSteps(RICH_TOUR)
    return () => {
      registerDemoRunner(null)
      controllerRef.current?.cancel()
    }
  }, [registerDemoRunner, setTourSteps, runCinematic])

  return (
    <>
      <CinematicStageView
        stage={active ? stage : null}
        routeRef={routeRef}
        onRouteArrive={onRouteArrive}
        onRouteComplete={onRouteComplete}
      />
      {active && (
        <CinematicCaption
          chapter={chapter}
          title={title}
          text={text}
          paused={paused}
          onTogglePause={() => controllerRef.current?.togglePause()}
          onSkip={() => controllerRef.current?.skip()}
          onClose={stop}
        />
      )}
    </>
  )
}
